/**
 * BISI Engineering Website - Asset Configuration
 * 
 * This file contains all image references used throughout the website.
 * Images are automatically loaded from the assets/images folder structure.
 * 
 * To add images:
 * 1. Place images in the appropriate folder: src/assets/images/{category}/
 * 2. Images will be automatically discovered and available
 * 3. No code changes needed - just add the image file!
 * 
 * Folder structure:
 * - brand/     - Company logos
 * - hero/      - Hero section backgrounds
 * - team/      - Team member photos
 * - projects/  - Project showcase images
 * - gallery/   - Gallery images
 * - testimonials/ - Testimonial author photos
 * - services/  - Service-related images
 * - clients/   - Client logos
 */

import { getImage, getImageByIndex, getPlaceholder } from '../lib/imageLoader';

// =============================================================================
// BRAND ASSETS
// =============================================================================

// Support both 'logo' and 'bisi-logo' - use whichever exists
const brandLogo = getImage('brand', 'logo', { width: 150, height: 60, placeholderText: 'BISI Engineering Logo' });
const brandLogoFallback = getImage('brand', 'bisi-logo', { width: 150, height: 60, placeholderText: 'BISI Engineering Logo' });

export const BRAND = {
  logo: brandLogo?.includes?.('via.placeholder') ? brandLogoFallback : brandLogo,
  logoAlt: "BISI Engineering",
  companyName: "BISI Engineering",
};

// =============================================================================
// HERO SECTION
// =============================================================================

export const HERO = {
  backgroundImage: getImage('hero', 'hero-bg', { width: 1920, height: 1080, placeholderText: 'Hero Background' }),
  aboutHero: getImage('hero', 'about-hero-bg', { width: 1920, height: 1080, placeholderText: 'About Hero Background' }),
};

// =============================================================================
// HOME PAGE IMAGES
// =============================================================================

export const HOME_IMAGES = {
  industry: getImage('home', 'Industry', { width: 800, height: 450, placeholderText: 'Why industry leaders choose us' }),
};

// =============================================================================
// TEAM MEMBER IMAGES
// =============================================================================

export const TEAM_IMAGES = {
  mdPhoto: getImage('team', 'md-photo', { width: 500, height: 600, placeholderText: 'Managing Director' }),
  member1: getImage('team', 'member-1', { width: 400, height: 400, placeholderText: 'Team Member 1' }),
  member2: getImage('team', 'Uttam-Kumar-Sethi', { width: 400, height: 400, placeholderText: 'Uttam Kumar Sethi' }),
  member3: getImage('team', 'member-3', { width: 400, height: 400, placeholderText: 'Team Member 3' }),
  member4: getImage('team', 'Ram-Chandra-Majhi', { width: 400, height: 400, placeholderText: 'Ram Chandra Majhi' }),
  member5: getImage('team', 'member-5', { width: 400, height: 400, placeholderText: 'Team Member 5' }),
  member6: getImage('team', 'member-6', { width: 400, height: 400, placeholderText: 'Team Member 6' }),
};

// =============================================================================
// PROJECT IMAGES
// =============================================================================

// Recent (Ongoing) 1–12, Past 13–14
export const PROJECT_IMAGES = {
  project1: getImage('projects', 'project-1', { width: 600, height: 400, placeholderText: 'Project 1' }),
  project2: getImage('projects', 'project-2', { width: 600, height: 400, placeholderText: 'Project 2' }),
  project3: getImage('projects', 'project-3', { width: 600, height: 400, placeholderText: 'Project 3' }),
  project4: getImage('projects', 'project-4', { width: 600, height: 400, placeholderText: 'Project 4' }),
  project5: getImage('projects', 'project-5', { width: 600, height: 400, placeholderText: 'Project 5' }),
  project6: getImage('projects', 'project-6', { width: 600, height: 400, placeholderText: 'Project 6' }),
  project7: getImage('projects', 'project-7', { width: 600, height: 400, placeholderText: 'Project 7' }),
  project8: getImage('projects', 'project-8', { width: 600, height: 400, placeholderText: 'Project 8' }),
  project9: getImage('projects', 'project-9', { width: 600, height: 400, placeholderText: 'Project 9' }),
  project10: getImage('projects', 'project-10', { width: 600, height: 400, placeholderText: 'Project 10' }),
  project11: getImage('projects', 'project-11', { width: 600, height: 400, placeholderText: 'Project 11' }),
  project12: getImage('projects', 'project-12', { width: 600, height: 400, placeholderText: 'Project 12' }),
  project13: getImage('projects', 'project-13', { width: 600, height: 400, placeholderText: 'Past Project 1' }),
  project14: getImage('projects', 'project-14', { width: 600, height: 400, placeholderText: 'Past Project 2' }),
};

// =============================================================================
// TESTIMONIAL IMAGES
// =============================================================================

export const TESTIMONIAL_IMAGES = {
  client1: getImage('testimonials', 'client-1', { width: 100, height: 100, placeholderText: 'Client 1' }),
  client2: getImage('testimonials', 'client-2', { width: 100, height: 100, placeholderText: 'Client 2' }),
  client3: getImage('testimonials', 'client-3', { width: 100, height: 100, placeholderText: 'Client 3' }),
  client4: getImage('testimonials', 'client-4', { width: 100, height: 100, placeholderText: 'Client 4' }),
};

// =============================================================================
// FEATURED SECTION IMAGES (Republic Day Celebration)
// =============================================================================

export const FEATURED_IMAGES = {
  featured1: getImage('gallery', 'Featured1', { width: 600, height: 400, placeholderText: 'Republic Day 1' }),
  featured2: getImage('gallery', 'Featured2', { width: 600, height: 400, placeholderText: 'Republic Day 2' }),
  featured3: getImage('gallery', 'Featured3', { width: 600, height: 400, placeholderText: 'Republic Day 3' }),
  featured4: getImage('gallery', 'Featured4', { width: 600, height: 400, placeholderText: 'Republic Day 4' }),
  featured5: getImage('gallery', 'Featured5', { width: 600, height: 400, placeholderText: 'Republic Day 5' }),
};

// =============================================================================
// GALLERY IMAGES
// =============================================================================

export const GALLERY_IMAGES = {
  gallery1: getImage('gallery', 'gallery-1', { width: 600, height: 400, placeholderText: 'Gallery 1' }),
  gallery2: getImage('gallery', 'gallery-2', { width: 600, height: 400, placeholderText: 'Gallery 2' }),
  gallery3: getImage('gallery', 'gallery-3', { width: 600, height: 400, placeholderText: 'Gallery 3' }),
  gallery4: getImage('gallery', 'gallery-4', { width: 600, height: 400, placeholderText: 'Gallery 4' }),
  gallery5: getImage('gallery', 'gallery-5', { width: 600, height: 400, placeholderText: 'Gallery 5' }),
  gallery6: getImage('gallery', 'gallery-6', { width: 600, height: 400, placeholderText: 'Gallery 6' }),
  gallery7: getImage('gallery', 'gallery-7', { width: 600, height: 400, placeholderText: 'Gallery 7' }),
  gallery8: getImage('gallery', 'gallery-8', { width: 600, height: 400, placeholderText: 'Gallery 8' }),
  gallery9: getImage('gallery', 'gallery-9', { width: 600, height: 400, placeholderText: 'Gallery 9' }),
  gallery10: getImage('gallery', 'gallery-10', { width: 600, height: 400, placeholderText: 'Gallery 10' }),
};

// =============================================================================
// SERVICE IMAGES
// =============================================================================

export const SERVICE_IMAGES = {
  service1: getImage('services', '1', { width: 600, height: 400, placeholderText: 'Erection & Installation' }),
  service2: getImage('services', '2', { width: 600, height: 400, placeholderText: 'Boiler Systems' }),
  service3: getImage('services', '3', { width: 600, height: 400, placeholderText: 'Maintenance & Overhauling' }),
  service4: getImage('services', '4', { width: 600, height: 400, placeholderText: 'Pipeline Works' }),
  service5: getImage('services', '5', { width: 600, height: 400, placeholderText: 'Commissioning' }),
  service6: getImage('services', '6', { width: 600, height: 400, placeholderText: 'Revamping & Renovation' }),
};

// =============================================================================
// CLIENT LOGOS
// =============================================================================

export const CLIENT_LOGOS = {
  acc: getImage('clients', 'acc', { width: 150, height: 80, placeholderText: 'ACC' }),
  aditya: getImage('clients', 'aditya', { width: 150, height: 80, placeholderText: 'Aditya' }),
  ambuja: getImage('clients', 'ambuja', { width: 150, height: 80, placeholderText: 'Ambuja' }),
  aryan: getImage('clients', 'aryan', { width: 150, height: 80, placeholderText: 'Aryan' }),
  bhavani: getImage('clients', 'bhavani', { width: 150, height: 80, placeholderText: 'Bhavani' }),
  bhel: getImage('clients', 'bhel', { width: 150, height: 80, placeholderText: 'BHEL' }),
  bpsl: getImage('clients', 'bpsl', { width: 150, height: 80, placeholderText: 'BPSL' }),
  facor: getImage('clients', 'facor', { width: 150, height: 80, placeholderText: 'FACOR' }),
  flsmidth: getImage('clients', 'flsmidth', { width: 150, height: 80, placeholderText: 'FLSmidth' }),
  gannon: getImage('clients', 'gannon', { width: 150, height: 80, placeholderText: 'Gannon' }),
  hindalco: getImage('clients', 'hindalco', { width: 150, height: 80, placeholderText: 'Hindalco' }),
  jsl: getImage('clients', 'jsl', { width: 150, height: 80, placeholderText: 'JSL' }),
  jspl: getImage('clients', 'jspl', { width: 150, height: 80, placeholderText: 'JSPL' }),
  lt: getImage('clients', 'lt', { width: 150, height: 80, placeholderText: 'L&T' }),
  macawber: getImage('clients', 'macawber', { width: 150, height: 80, placeholderText: 'Macawber' }),
  mecgale: getImage('clients', 'mecgale', { width: 150, height: 80, placeholderText: 'Mecgale' }),
  ntpc: getImage('clients', 'ntpc', { width: 150, height: 80, placeholderText: 'NTPC' }),
  opgc: getImage('clients', 'opgc', { width: 150, height: 80, placeholderText: 'OPGC' }),
  powerMax: getImage('clients', 'power-max', { width: 150, height: 80, placeholderText: 'Power Max' }),
  spiegel: getImage('clients', 'spiegel', { width: 150, height: 80, placeholderText: 'Spiegel' }),
  vedanta: getImage('clients', 'vedanta', { width: 150, height: 80, placeholderText: 'Vedanta' }),
};

// =============================================================================
// PLACEHOLDER IMAGES (for fallbacks)
// =============================================================================

export const PLACEHOLDERS = {
  avatar: getPlaceholder(100, 100, 'Avatar'),
  project: getPlaceholder(600, 400, 'Project'),
  logo: getPlaceholder(150, 60, 'Logo'),
};
